import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { ShieldCheck, Banknote, Gauge, Building2, FileText, Check, Flame } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Logo, ShieldMark } from "@/components/Logo";
import { SiteHeader } from "@/components/SiteHeader";
import { AudienceToggle } from "@/components/AudienceToggle";
import { Seo, SITE_URL } from "@/components/Seo";
import { getConfig } from "@/lib/api";
import { IdeaEntry } from "@/components/IdeaEntry";
import { PreviewModal } from "@/components/PreviewModal";

const features = [
  {
    icon: Gauge,
    title: "Standortbestimmung",
    desc: "Ehrliche Einordnung von Marktposition, Preismodell und Wachstumshebeln deines laufenden Business.",
    color: "var(--brand-solid)",
  },
  {
    icon: Banknote,
    title: "Finanzierung & Kapital",
    desc: "Welche Finanzierungswege – Bankkredit, Betriebsmittel, Leasing oder Investoren – für dein Wachstum realistisch in Frage kommen können.",
    color: "var(--brand-g3)",
  },
  {
    icon: ShieldCheck,
    title: "Optimierungs-Hinweise",
    desc: "Konkrete Ansatzpunkte zu Neukundengewinnung, Bürokratie/Recht und Skalierung – auf dein Angebot zugeschnitten.",
    color: "#F59E0B",
  },
];

const reportContents = [
  "Ehrliche, qualitative Gesamteinschätzung für dein laufendes Business",
  "Wo dein größtes Wachstums- & Optimierungspotenzial liegt",
  "Finanzierungs-Check: Bankkredit, Leasing & Kapital für dein Wachstum",
  "Hinweise zu Neukundengewinnung, Preisstrategie & Skalierung",
  "Konkreter 30-Tage-Plan mit den nächsten sinnvollen Schritten",
  "Strukturiertes PDF (über 10 Seiten) zum Download & per E-Mail",
];

export default function Business() {
  const nav = useNavigate();
  const [counter, setCounter] = useState(null);
  useEffect(() => {
    getConfig().then((c) => setCounter({ remaining: c.business_remaining, limit: c.business_limit })).catch(() => {});
  }, []);

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Product",
      name: "Gründio-Report für bestehende Unternehmen (PDF)",
      image: `${SITE_URL}/gruendio-logo.png`,
      brand: { "@type": "Brand", name: "Gründio" },
      description: "Strukturierter KI-Report zur Standortbestimmung und Optimierung eines bestehenden Unternehmens – mit Wachstumshebeln, Finanzierungs-Orientierung und 30-Tage-Plan.",
      offers: { "@type": "Offer", url: `${SITE_URL}/business`, priceCurrency: "EUR", price: "39.00", availability: "https://schema.org/InStock" },
    },
  ];

  return (
    <div className="min-h-screen theme-petrol">
      <Seo
        title="Gründio-Report für bestehende Unternehmen | Business-Check in 2 Minuten"
        description="Du hast bereits ein Unternehmen? Der Gründio-Report zeigt dir in 2 Minuten, wo du optimieren und wachsen kannst – mit ehrlicher Einschätzung, Wachstumshebeln, Finanzierungs-Orientierung und 30-Tage-Plan. Keine Rechts- oder Steuerberatung."
        path="/business"
        keywords="Business optimieren, Unternehmen analysieren, Wachstum KMU, Unternehmen finanzieren, Betriebsmittelkredit"
        jsonLd={jsonLd}
      />
      <SiteHeader />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-dots pointer-events-none opacity-60" />
        <div className="relative max-w-6xl mx-auto px-6 pt-16 pb-24 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 animate-fade-in-up">
            <div className="mb-6"><AudienceToggle /></div>

            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-slate-200/80 text-xs uppercase tracking-widest text-slate-600 mb-6 shadow-sm">
              <span className="h-1.5 w-1.5 rounded-full brand-gradient-bg" />
              Für bestehende Unternehmen in Deutschland
            </div>

            {counter && counter.remaining > 0 && (
              <div
                data-testid="business-discount-counter"
                className="inline-flex items-center gap-2 mb-6 px-3 py-1 rounded-full bg-amber-50 border border-amber-200 text-xs font-semibold text-amber-800"
              >
                <Flame className="h-3.5 w-3.5 text-amber-500" />
                Einführungspreis 39 € statt 49 € · nur für die ersten {counter.limit} · <b data-testid="business-remaining">noch {counter.remaining} frei</b>
              </div>
            )}

            <h1
              className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] hyphens-none"
              style={{ fontFamily: "Outfit, sans-serif" }}
            >
              <span className="text-slate-900">Der </span>
              <span className="brand-gradient-text">Gründio-Report</span>
              <span className="text-slate-900"> für dein bestehendes Business.</span>
            </h1>

            <p className="mt-6 text-lg text-slate-600 max-w-xl leading-relaxed">
              Du willst dein Unternehmen aufs nächste Level bringen? In ca. 60 Sekunden bekommst du <b className="text-slate-900">kostenlos eine erste
              Kurz-Einschätzung</b> und drei ehrliche Kurz-Urteile. Der <b className="text-slate-900">vollständige Gründio-Report</b> zeigt
              dir für 39 €, <b className="text-slate-900">an welchen Stellschrauben du drehen</b> kannst, um zu optimieren und weiter zu wachsen.
            </p>

            <div className="mt-8">
              <IdeaEntry compact variant="business" />
            </div>

            <p data-testid="business-trust-line" className="mt-3 flex items-center gap-2 text-sm font-medium text-slate-600">
              <ShieldCheck className="h-4 w-4 shrink-0" style={{ color: "var(--brand-solid)" }} />
              Ersteinschätzung gratis · Keine Bezahldaten · Kein Login
            </p>

            <p className="mt-4 text-xs text-slate-500 max-w-xl leading-relaxed">
              Automatisierte Orientierungshilfe & KI-gestütztes Express-Feedback. Keine Rechts- oder Steuerberatung.
            </p>
          </div>

          {/* RIGHT: report contents card */}
          <div className="lg:col-span-5 animate-fade-in-up">
            <div className="relative">
              <div className="absolute -inset-2 brand-gradient-bg opacity-30 blur-2xl rounded-3xl" />
              <div className="relative brand-gradient-border rounded-3xl p-7 shadow-2xl bg-white">
                <div className="flex items-center justify-between mb-5">
                  <span className="text-xs uppercase tracking-widest text-slate-500">Dein Gründio-Report enthält</span>
                  <span className="text-xs px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 font-medium">10+ Seiten</span>
                </div>
                <ul className="space-y-3">
                  {reportContents.map((t) => (
                    <li key={t} className="flex items-start gap-3 text-[15px] text-slate-700">
                      <span className="mt-0.5 h-5 w-5 rounded-full brand-gradient-bg flex items-center justify-center shrink-0">
                        <Check className="h-3 w-3 text-white" />
                      </span>
                      <span>{t}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-6 pt-5 border-t border-slate-200 flex items-end justify-between">
                  <div>
                    <div className="text-xs uppercase tracking-widest text-slate-500">Einführungspreis</div>
                    <div className="flex items-baseline gap-2">
                      <div className="text-3xl font-extrabold text-slate-900" style={{ fontFamily: "Outfit" }}>39,00 €</div>
                      <div className="text-sm text-slate-400 line-through">49 €</div>
                    </div>
                  </div>
                  <div className="text-xs text-slate-500 text-right">Sofort-Download<br/>& per E-Mail</div>
                </div>
                <div className="mt-4 flex justify-center">
                  <PreviewModal
                    variant="business"
                    triggerLabel="Vorschau ansehen"
                    ctaLabel="Jetzt Check starten"
                    onCta={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                    order={[1, 3, 22, 23, 20, 21, 12, 13, 9, 10, 2, 4, 5, 6, 7, 8, 11, 14, 15, 16, 17, 18, 19]}
                    labels={{
                      1: "Überblick",
                      3: "Gesamteinschätzung",
                      22: "30-Tage-Plan",
                      23: "30-Tage-Plan",
                      20: "Sparring-Fragen",
                      21: "Sparring-Fragen",
                      12: "Finanzierung",
                      13: "Finanzierung",
                      9: "Chancen & Risiken",
                      10: "Chancen & Risiken",
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-6xl mx-auto px-6 pb-20">
        <div className="grid md:grid-cols-3 gap-5">
          {features.map((f) => (
            <div key={f.title} className="bg-white rounded-2xl p-6 border border-slate-200/70 hover:border-emerald-300 hover:shadow-lg transition-all">
              <div className="h-11 w-11 rounded-xl flex items-center justify-center mb-4" style={{ background: `${f.color}18`, color: f.color }}>
                <f.icon className="h-5 w-5" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: "Outfit" }}>{f.title}</h3>
              <p className="text-sm text-slate-600 mt-2 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>

        {/* How it works */}
        <div className="mt-20 grid md:grid-cols-3 gap-5">
          {[
            { n: "1", title: "Business kurz beschreiben", desc: "Angebot, Standort, Zielgruppe, Kennzahlen – in ca. 60 Sek. ausgefüllt." },
            { n: "2", title: "Strukturiertes Feedback", desc: "Sofort eine ehrliche, strukturierte Ersteinschätzung zu deinem Unternehmen." },
            { n: "3", title: "Kompletter Gründio-Report", desc: "PDF (über 10 Seiten) für 39 €: Wachstumshebel, Finanzierungs-Orientierung & 30-Tage-Plan." },
          ].map((s) => (
            <div key={s.n} className="relative pl-14">
              <div className="absolute left-0 top-0 h-10 w-10 rounded-xl text-white font-bold flex items-center justify-center brand-gradient-bg shadow-md" style={{ fontFamily: "Outfit" }}>
                {s.n}
              </div>
              <h4 className="text-lg font-semibold text-slate-900" style={{ fontFamily: "Outfit" }}>{s.title}</h4>
              <p className="text-sm text-slate-600 mt-1">{s.desc}</p>
            </div>
          ))}
        </div>

        {/* Bottom CTA dark plate */}
        <div className="mt-20 relative rounded-3xl overflow-hidden">
          <div className="absolute inset-0 bg-slate-900" />
          <div className="absolute -top-32 -right-32 h-72 w-72 rounded-full opacity-30 brand-gradient-bg blur-3xl" />
          <div className="absolute -bottom-32 -left-32 h-72 w-72 rounded-full opacity-20 bg-emerald-400 blur-3xl" />
          <div className="relative p-10 text-white grid md:grid-cols-[auto,1fr,auto] gap-6 items-center">
            <div className="hidden md:block"><ShieldMark size={64} /></div>
            <div>
              <div className="text-xs uppercase tracking-widest text-emerald-300 mb-2 flex items-center gap-2">
                <Building2 className="h-4 w-4" /> Business-Check
              </div>
              <h2 className="text-2xl md:text-3xl font-semibold" style={{ fontFamily: "Outfit" }}>
                Wo steht dein Unternehmen – und wo ist der nächste Hebel?
              </h2>
              <p className="text-white/70 mt-3 max-w-2xl">
                Über 10 Seiten Feedback mit Wachstumshebeln, Finanzierungs-Orientierung und 30-Tage-Plan – zum Download.
              </p>
            </div>
            <Button
              data-testid="business-cta-bottom"
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
              className="h-14 px-7 rounded-xl font-semibold brand-gradient-bg text-white hover:opacity-95"
            >
              <FileText className="h-5 w-5 mr-2" /> Check starten
            </Button>
          </div>
        </div>
      </section>

      <footer className="border-t border-slate-200/70 bg-white/60">
        <div className="max-w-6xl mx-auto px-6 py-8 text-xs text-slate-500 grid sm:grid-cols-2 gap-4 items-start">
          <div className="space-y-2">
            <Logo size="sm" />
            <p className="max-w-md leading-relaxed">
              Gründio bietet eine automatisierte Orientierungshilfe und KI-gestützte
              Erstbewertung. <b>Keine Rechts- oder Steuerberatung</b>.
            </p>
          </div>
          <div className="sm:text-right space-y-2">
            <nav className="flex sm:justify-end items-center gap-4 flex-wrap">
              <a href="/impressum" className="hover:text-slate-900">Impressum</a>
              <a href="/datenschutz" className="hover:text-slate-900">Datenschutz</a>
              <a href="/agb" className="hover:text-slate-900">AGB</a>
              <a href="/widerruf" className="hover:text-slate-900">Widerruf</a>
            </nav>
            <div>© {new Date().getFullYear()} Gründio · Stand 2026</div>
          </div>
        </div>
      </footer>
    </div>
  );
}
